import {
  type CaseFormActionItemKey,
  type CaseFormTemplateQuestionKey,
  type CaseType,
  type TextQuestionIcon,
} from '@repo/internal-tools-api/src/services/cases/types.ts'
import type { RadioOption } from '@repo/internal-tools-design-system/src/lib/RadioGroupField/RadioGroupField.tsx'
import type {
  EnumKey,
  ExtractKeys,
} from '@repo/internal-tools-type-utils/src/utils.ts'
import { type ReactNode } from 'react'
import type {
  CardCurrentStatus,
  DisputeInFavorOfCmOption,
  DisputeReasons,
} from './types.ts'

export const FORM_TEMPLATE_VERSION = {
  CardDispute: 4,
  MisdirectedDeposit: 3,
  CreditReportDispute: 2,
  FinCrimes: 2,
  ProofOfAuth: 1,
  VisaRiskManager: -1,
} as const satisfies Record<CaseType, number>

export const CASE_TYPE = {
  CARD_DISPUTE: 'CardDispute',
  MISDIRECTED_DEPOSIT: 'MisdirectedDeposit',
  CREDIT_REPORT_DISPUTE: 'CreditReportDispute',
  FIN_CRIMES: 'FinCrimes',
  PROOF_OF_AUTH: 'ProofOfAuth',
  VISA_RISK_MANAGER: 'VisaRiskManager',
} as const satisfies Record<EnumKey, CaseType>

export const CARD_DISPUTE_V1_QUESTION_KEYS = {
  QUESTION_1: 'CardDisputeV1Question1',
  QUESTION_2: 'CardDisputeV1Question2',
  QUESTION_3: 'CardDisputeV1Question3',
  QUESTION_4: 'CardDisputeV1Question4',
  QUESTION_5: 'CardDisputeV1Question5',
  QUESTION_6: 'CardDisputeV1Question6',
  QUESTION_7: 'CardDisputeV1Question7',
  QUESTION_8: 'CardDisputeV1Question8',
  QUESTION_9: 'CardDisputeV1Question9',
  QUESTION_10: 'CardDisputeV1Question10',
  QUESTION_11: 'CardDisputeV1Question11',
  QUESTION_12: 'CardDisputeV1Question12',
  QUESTION_13: 'CardDisputeV1Question13',
  QUESTION_14: 'CardDisputeV1Question14',
  QUESTION_15: 'CardDisputeV1Question15',
  QUESTION_16: 'CardDisputeV1Question16',
  QUESTION_17: 'CardDisputeV1Question17',
  QUESTION_18: 'CardDisputeV1Question18',
  QUESTION_19: 'CardDisputeV1Question19',
  QUESTION_20: 'CardDisputeV1Question20',
  QUESTION_21: 'CardDisputeV1Question21',
  QUESTION_22: 'CardDisputeV1Question22',
  QUESTION_23: 'CardDisputeV1Question23',
  QUESTION_24: 'CardDisputeV1Question24',
  QUESTION_25: 'CardDisputeV1Question25',
  QUESTION_26: 'CardDisputeV1Question26',
  QUESTION_27: 'CardDisputeV1Question27',
  QUESTION_28: 'CardDisputeV1Question28',
  QUESTION_29: 'CardDisputeV1Question29',
  QUESTION_30: 'CardDisputeV1Question30',
  QUESTION_31: 'CardDisputeV1Question31',
  QUESTION_32: 'CardDisputeV1Question32',
  QUESTION_33: 'CardDisputeV1Question33',
  QUESTION_34: 'CardDisputeV1Question34',
  QUESTION_35: 'CardDisputeV1Question35',
} as const satisfies Record<
  EnumKey,
  ExtractKeys<CaseFormTemplateQuestionKey, 'CardDisputeV1'>
>

export const CARD_DISPUTE_V1_ACTION_ITEM_KEYS = {
  ACTION_1: 'CardDisputeV1Action1',
  ACTION_2: 'CardDisputeV1Action2',
  ACTION_3: 'CardDisputeV1Action3',
  ACTION_4: 'CardDisputeV1Action4',
  ACTION_5: 'CardDisputeV1Action5',
  ACTION_6: 'CardDisputeV1Action6',
  ACTION_8: 'CardDisputeV1Action8',
  ACTION_9: 'CardDisputeV1Action9',
  ACTION_10: 'CardDisputeV1Action10',
  ACTION_11: 'CardDisputeV1Action11',
} as const satisfies Record<
  EnumKey,
  ExtractKeys<CaseFormActionItemKey, 'CardDisputeV1'>
>

export const CARD_DISPUTE_V2_QUESTION_KEYS = {
  QUESTION_1: 'CardDisputeV2Question1',
  QUESTION_2: 'CardDisputeV2Question2',
  QUESTION_3: 'CardDisputeV2Question3',
  QUESTION_4: 'CardDisputeV2Question4',
  QUESTION_5: 'CardDisputeV2Question5',
  QUESTION_6: 'CardDisputeV2Question6',
  QUESTION_7: 'CardDisputeV2Question7',
  QUESTION_8: 'CardDisputeV2Question8',
  QUESTION_9: 'CardDisputeV2Question9',
  QUESTION_10: 'CardDisputeV2Question10',
  QUESTION_11: 'CardDisputeV2Question11',
  QUESTION_12: 'CardDisputeV2Question12',
  QUESTION_13: 'CardDisputeV2Question13',
  QUESTION_14: 'CardDisputeV2Question14',
  QUESTION_15: 'CardDisputeV2Question15',
  QUESTION_16: 'CardDisputeV2Question16',
  QUESTION_17: 'CardDisputeV2Question17',
  QUESTION_18: 'CardDisputeV2Question18',
  QUESTION_19: 'CardDisputeV2Question19',
  QUESTION_20: 'CardDisputeV2Question20',
  QUESTION_21: 'CardDisputeV2Question21',
  QUESTION_22: 'CardDisputeV2Question22',
  QUESTION_23: 'CardDisputeV2Question23',
  QUESTION_24: 'CardDisputeV2Question24',
  QUESTION_25: 'CardDisputeV2Question25',
  QUESTION_26: 'CardDisputeV2Question26',
  QUESTION_27: 'CardDisputeV2Question27',
  QUESTION_28: 'CardDisputeV2Question28',
  QUESTION_29: 'CardDisputeV2Question29',
  QUESTION_30: 'CardDisputeV2Question30',
  QUESTION_31: 'CardDisputeV2Question31',
  QUESTION_32: 'CardDisputeV2Question32',
  QUESTION_33: 'CardDisputeV2Question33',
  QUESTION_34: 'CardDisputeV2Question34',
  QUESTION_35: 'CardDisputeV2Question35',
} as const satisfies Record<
  EnumKey,
  ExtractKeys<CaseFormTemplateQuestionKey, 'CardDisputeV2'>
>

export const CARD_DISPUTE_V2_ACTION_ITEM_KEYS = {
  ACTION_1: 'CardDisputeV2Action1',
  ACTION_2: 'CardDisputeV2Action2',
  ACTION_3: 'CardDisputeV2Action3',
  ACTION_4: 'CardDisputeV2Action4',
  ACTION_5: 'CardDisputeV2Action5',
  ACTION_6: 'CardDisputeV2Action6',
  ACTION_8: 'CardDisputeV2Action8',
  ACTION_9: 'CardDisputeV2Action9',
  ACTION_10: 'CardDisputeV2Action10',
  ACTION_11: 'CardDisputeV2Action11',
  ACTION_12: 'CardDisputeV2Action12',
} as const satisfies Record<
  EnumKey,
  ExtractKeys<CaseFormActionItemKey, 'CardDisputeV2'>
>

export const CARD_DISPUTE_V3_QUESTION_KEYS = {
  QUESTION_1: 'CardDisputeV3Question1',
  QUESTION_2: 'CardDisputeV3Question2',
  QUESTION_3: 'CardDisputeV3Question3',
  QUESTION_4: 'CardDisputeV3Question4',
  QUESTION_5: 'CardDisputeV3Question5',
  QUESTION_6: 'CardDisputeV3Question6',
  QUESTION_7: 'CardDisputeV3Question7',
  QUESTION_8: 'CardDisputeV3Question8',
  QUESTION_9: 'CardDisputeV3Question9',
  QUESTION_10: 'CardDisputeV3Question10',
  QUESTION_11: 'CardDisputeV3Question11',
  QUESTION_13: 'CardDisputeV3Question13',
  QUESTION_14: 'CardDisputeV3Question14',
  QUESTION_15: 'CardDisputeV3Question15',
  QUESTION_16: 'CardDisputeV3Question16',
  QUESTION_17: 'CardDisputeV3Question17',
  QUESTION_18: 'CardDisputeV3Question18',
  QUESTION_19: 'CardDisputeV3Question19',
  QUESTION_20: 'CardDisputeV3Question20',
  QUESTION_21: 'CardDisputeV3Question21',
  QUESTION_22: 'CardDisputeV3Question22',
  QUESTION_23: 'CardDisputeV3Question23',
  QUESTION_24: 'CardDisputeV3Question24',
  QUESTION_25: 'CardDisputeV3Question25',
  QUESTION_26: 'CardDisputeV3Question26',
  QUESTION_27: 'CardDisputeV3Question27',
  QUESTION_28: 'CardDisputeV3Question28',
  QUESTION_29: 'CardDisputeV3Question29',
  QUESTION_30: 'CardDisputeV3Question30',
  QUESTION_31: 'CardDisputeV3Question31',
  QUESTION_32: 'CardDisputeV3Question32',
  QUESTION_33: 'CardDisputeV3Question33',
  QUESTION_34: 'CardDisputeV3Question34',
  QUESTION_35: 'CardDisputeV3Question35',
} as const satisfies Record<
  EnumKey,
  ExtractKeys<CaseFormTemplateQuestionKey, 'CardDisputeV3'>
>

export const CARD_DISPUTE_V3_ACTION_ITEM_KEYS = {
  ACTION_1: 'CardDisputeV3Action1',
  ACTION_2: 'CardDisputeV3Action2',
  ACTION_3: 'CardDisputeV3Action3',
  ACTION_4: 'CardDisputeV3Action4',
  ACTION_5: 'CardDisputeV3Action5',
  ACTION_6: 'CardDisputeV3Action6',
  ACTION_8: 'CardDisputeV3Action8',
  ACTION_9: 'CardDisputeV3Action9',
  ACTION_10: 'CardDisputeV3Action10',
  ACTION_11: 'CardDisputeV3Action11',
  ACTION_12: 'CardDisputeV3Action12',
  ACTION_13: 'CardDisputeV3Action13',
} as const satisfies Record<
  EnumKey,
  ExtractKeys<CaseFormActionItemKey, 'CardDisputeV3'>
>

export const CARD_DISPUTE_V4_QUESTION_KEYS = {
  QUESTION_1: 'CardDisputeV4Question1',
  QUESTION_2: 'CardDisputeV4Question2',
  QUESTION_3: 'CardDisputeV4Question3',
  QUESTION_4: 'CardDisputeV4Question4',
  QUESTION_5: 'CardDisputeV4Question5',
  QUESTION_6: 'CardDisputeV4Question6',
  QUESTION_7: 'CardDisputeV4Question7',
  QUESTION_8: 'CardDisputeV4Question8',
  QUESTION_9: 'CardDisputeV4Question9',
  QUESTION_10: 'CardDisputeV4Question10',
  QUESTION_11: 'CardDisputeV4Question11',
  QUESTION_13: 'CardDisputeV4Question13',
  QUESTION_14: 'CardDisputeV4Question14',
  QUESTION_15: 'CardDisputeV4Question15',
  QUESTION_16: 'CardDisputeV4Question16',
  QUESTION_17: 'CardDisputeV4Question17',
  QUESTION_18: 'CardDisputeV4Question18',
  QUESTION_19: 'CardDisputeV4Question19',
  QUESTION_20: 'CardDisputeV4Question20',
  QUESTION_21: 'CardDisputeV4Question21',
  QUESTION_22: 'CardDisputeV4Question22',
  QUESTION_23: 'CardDisputeV4Question23',
  QUESTION_24: 'CardDisputeV4Question24',
  QUESTION_25: 'CardDisputeV4Question25',
  QUESTION_26: 'CardDisputeV4Question26',
  QUESTION_27: 'CardDisputeV4Question27',
  QUESTION_28: 'CardDisputeV4Question28',
  QUESTION_29: 'CardDisputeV4Question29',
  QUESTION_30: 'CardDisputeV4Question30',
  QUESTION_31: 'CardDisputeV4Question31',
  QUESTION_32: 'CardDisputeV4Question32',
  QUESTION_33: 'CardDisputeV4Question33',
  QUESTION_34: 'CardDisputeV4Question34',
  QUESTION_35: 'CardDisputeV4Question35',
} as const satisfies Record<
  EnumKey,
  ExtractKeys<CaseFormTemplateQuestionKey, 'CardDisputeV4'>
>

export const CARD_DISPUTE_V4_ACTION_ITEM_KEYS = {
  ACTION_1: 'CardDisputeV4Action1',
  ACTION_2: 'CardDisputeV4Action2',
  ACTION_3: 'CardDisputeV4Action3',
  ACTION_4: 'CardDisputeV4Action4',
  ACTION_5: 'CardDisputeV4Action5',
  ACTION_6: 'CardDisputeV4Action6',
  ACTION_8: 'CardDisputeV4Action8',
  ACTION_9: 'CardDisputeV4Action9',
  ACTION_10: 'CardDisputeV4Action10',
  ACTION_11: 'CardDisputeV4Action11',
  ACTION_12: 'CardDisputeV4Action12',
  ACTION_13: 'CardDisputeV4Action13',
} as const satisfies Record<
  EnumKey,
  ExtractKeys<CaseFormActionItemKey, 'CardDisputeV4'>
>

export const CARD_CURRENT_STATUS = {
  STOLEN_OR_LOST: 'stolenOrLost',
  IN_POSSESSION: 'inPossession',
} as const satisfies Record<EnumKey, CardCurrentStatus>

export const DISPUTE_REASONS = {
  EXPECTED_SERVICES_OR_MERCHANDISE_NOT_RECEIVED:
    'expectedServicesOrMerchandiseNotReceived',
  DEFECTIVE_MERCHANDISE_RECEIVED: 'defectiveMerchandiseReceived',
  DISPUTE_OVER_QUALITY_OF_SERVICES_RENDERED:
    'disputeOverQualityOfServicesRendered',
  RECURRING_TRANSACTIONS_NOT_CANCELED_AS_REQUESTED:
    'recurringTransactionsNotCanceledAsRequested',
  CANCELED_SERVICE_OR_MERCHANDISE_NOT_CREDITED:
    'canceledServiceOrMerchandiseNotCredited',
  PAID_BY_OTHER_MEANS: 'paidByOtherMeans',
  BILLED_TWICE_FOR_THE_SAME_TRANSACTION: 'billedTwiceForTheSameTransaction',
  AMOUNT_OF_PURCHASE_DIFFER_FROM_BILLED: 'amountOfPurchaseDifferFromBilled',
  NON_RECEIPT_OF_CASH: 'nonReceiptOfCash',
  OTHERS: 'others',
} as const satisfies Record<EnumKey, DisputeReasons>

export const DISPUTE_IN_FAVOR_OF_CM_OPTION_VALUES = {
  YES: 'yes',
  PARTIALLY: 'partially',
  NO: 'no',
} as const satisfies Record<EnumKey, DisputeInFavorOfCmOption>

export const YES_NO_RADIO_OPTIONS = [
  { label: 'Yes', value: 'true' },
  { label: 'No', value: 'false' },
] as const satisfies RadioOption[]

export const TEXT_QUESTION_ICON = {
  Dollar: '$',
} as const satisfies Record<TextQuestionIcon, ReactNode>
